"use client";

import { useId, useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import Link from "next/link";
import { identifyPosthogByEmail } from "@/lib/posthog-identify-email";

/**
 * The three success shapes `POST /api/newsletter` can answer with. Only
 * "subscribed" means a welcome mail actually went out, so the confirmation copy
 * has to follow the outcome instead of promising a mail on every 2xx.
 */
type SubscribeOutcome = "subscribed" | "already_active" | "opted_out";

const SUCCESS_MESSAGE_KEY: Record<SubscribeOutcome, string> = {
	subscribed: "success",
	already_active: "successAlreadyActive",
	opted_out: "successOptedOut",
};

interface NewsletterFormProps {
	privacyHref: string;
	compact?: boolean;
	channel?: string;
	source?: string;
	variant?: "default" | "hero";
	/** Display-currency-formatted discount amount (e.g. "5 €", "2.050 HUF") for the hero CTA label. */
	discountLabel?: string;
}

export function NewsletterForm({
	privacyHref,
	compact = false,
	channel,
	source = "footer",
	variant = "default",
	discountLabel,
}: NewsletterFormProps) {
	const t = useTranslations("home.newsletter");
	const locale = useLocale();
	const [email, setEmail] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [outcome, setOutcome] = useState<SubscribeOutcome>("subscribed");
	const [errorType, setErrorType] = useState<string | null>(null);
	const newsletterIdentifyDedupeRef = useRef<string | null>(null);
	const inputId = useId();
	const feedbackId = `${inputId}-feedback`;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorType(null);

		const emailTrimmed = email.trim();
		if (!emailTrimmed) {
			setErrorType("invalidEmail");
			return;
		}
		if (!consent) {
			setErrorType("consentRequired");
			return;
		}

		setStatus("loading");
		try {
			const res = await fetch("/api/newsletter", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: emailTrimmed, consent, locale, channel, source }),
			});
			const data = (await res.json()) as { error?: string; status?: string };

			if (!res.ok) {
				setStatus("error");
				const err = data?.error;
				setErrorType(
					err === "consent_required"
						? "consentRequired"
						: err === "invalid_email" || err === "email_required"
							? "invalidEmail"
							: "error",
				);
				return;
			}
			identifyPosthogByEmail(
				emailTrimmed,
				{ newsletter_signup: true, newsletter_source: source },
				newsletterIdentifyDedupeRef,
			);
			// An unknown or missing status keeps the old confirmation copy, so a client
			// served from cache before this deploy never renders a blank message.
			setOutcome(
				data?.status === "already_active" || data?.status === "opted_out" ? data.status : "subscribed",
			);
			setStatus("success");
			setEmail("");
			setConsent(false);
		} catch {
			setStatus("error");
			setErrorType("error");
		}
	};

	const isHero = variant === "hero";

	if (status === "success") {
		return (
			<p className={`text-center text-sm font-medium ${isHero ? "text-white" : "text-foreground"}`}>
				{t(SUCCESS_MESSAGE_KEY[outcome])}
			</p>
		);
	}

	const inputClass = isHero
		? "h-11 min-w-0 w-full rounded-md border border-white/20 bg-white/10 px-4 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-60"
		: "h-11 min-w-0 w-full rounded-md border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 sm:flex-[1_1_12rem]";

	const submitClass = isHero
		? "inline-flex min-h-11 w-full shrink-0 items-center justify-center whitespace-normal rounded-md bg-emerald-500 px-5 py-3 text-sm font-bold text-[var(--primary-hero)] shadow transition hover:bg-emerald-400 disabled:opacity-60"
		: "inline-flex min-h-11 shrink-0 items-center justify-center whitespace-normal rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60";

	const consentTextClass = isHero
		? `leading-relaxed text-white/80 ${compact ? "text-[11px]" : "text-xs"}`
		: `leading-relaxed text-muted-foreground ${compact ? "text-[11px]" : "text-xs"}`;

	const submitLabel =
		status === "loading"
			? "…"
			: isHero
				? t("subscribeHero", { amount: discountLabel ?? "" })
				: t("subscribe");

	return (
		// The explicit action/method only spell out what a browser already does for a form without
		// them (GET to the current page). Crawlers that resolve a missing action as the string
		// "null" requested <section>/null on every page. "#" needs no route hook, so the footer
		// keeps prerendering under Cache Components. The inputs carry no `name`, so even a
		// non-hydrated submit never puts the e-mail address into a URL.
		<form
			action="#"
			method="get"
			onSubmit={handleSubmit}
			className={`${isHero ? "w-full" : "mx-auto max-w-md"} ${compact ? "space-y-3" : "space-y-4"}`}
		>
			<div
				className={`${isHero ? "flex flex-col" : "flex flex-col sm:flex-row sm:flex-wrap"} ${compact ? "gap-2" : "gap-3"}`}
			>
				<label htmlFor={inputId} className="sr-only">
					{t("placeholder")}
				</label>
				<input
					id={inputId}
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder={t("placeholder")}
					disabled={status === "loading"}
					className={inputClass}
					aria-invalid={!!errorType}
					aria-describedby={errorType ? feedbackId : undefined}
				/>
				<button type="submit" disabled={status === "loading"} className={submitClass}>
					{submitLabel}
				</button>
			</div>
			<label className={`flex cursor-pointer items-start text-left ${compact ? "gap-2" : "gap-3"}`}>
				<input
					type="checkbox"
					checked={consent}
					onChange={(e) => setConsent(e.target.checked)}
					disabled={status === "loading"}
					className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border bg-background"
				/>
				<span className={consentTextClass}>
					{t("consentLabel")}
					<Link
						href={privacyHref}
						className={`underline underline-offset-2 ${isHero ? "text-white hover:text-emerald-300" : "hover:text-foreground"}`}
					>
						{t("privacyLink")}
					</Link>
					{t("consentLabelSuffix")}
				</span>
			</label>
			{errorType && (
				<p id={feedbackId} className={`text-xs ${isHero ? "text-red-300" : "text-destructive"}`} role="alert">
					{t(errorType)}
				</p>
			)}
		</form>
	);
}
